<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <template v-if="search">{{ $t("report_mobile.no_results_for", { search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_reserved_meals_data") }}
        <span class="rm-muted">{{ $t("select_date_and_company") }}</span>
      </template>
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("r_o") }}</span>
          <span class="rm-stat__value rm-num">{{ mealTotal }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("meals") }}</span>
          <span class="rm-stat__value rm-num">{{ rows.length }}</span>
        </div>
      </div>

      <ul class="rm-list rm-card">
        <li v-for="row in rows" :key="row.key" class="rm-mrow rm-mrow--action">
          <button type="button" class="rm-mrow__btn" @click="openWorkers(row.source)">
            <span class="rm-mrow__text">
              <span class="rm-mrow__main">
                <span class="rm-mrow__name">{{ row.name }}</span>
                <span class="rm-mrow__value rm-num">{{ row.total }}</span>
              </span>
              <span v-if="row.shifts.length" class="rm-mrow__shifts">
                <span v-for="shift in row.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                  {{ shift.quantity }}
                </span>
              </span>
            </span>
            <span class="rm-visually-hidden">{{ $t("show_employees_who_ordered") }}</span>
            <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
          </button>
        </li>
      </ul>
    </template>

    <ui-sheet v-model="workersSheet" :ready="Boolean(selectedMeal)" :label="$t('employees_who_ordered')">
      <template #title>
        {{ $t("employees_who_ordered") }}
        <small class="cm-sheet-sub">{{ toLatin(selectedMeal.name) }} · {{ periodText }}</small>
      </template>
      <template #default>
        <div v-if="workersLoading" class="rm-empty">
          <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
        </div>
        <div v-else-if="workersFailed" class="rm-empty">
          {{ $t("report_mobile.load_failed") }}
          <button type="button" class="rm-btn rm-btn--sm cm-retry" @click="loadWorkers">
            {{ $t("report_mobile.retry") }}
          </button>
        </div>
        <div v-else-if="!workers.length" class="rm-empty">
          <v-icon size="40" color="#8a817a">mdi-account-off</v-icon>
          {{ $t("no_employees_found") }}
          <span class="rm-muted">{{ $t("no_employees_ordered_this_meal") }}</span>
        </div>
        <ul v-else class="rm-list rm-card">
          <li v-for="worker in workers" :key="worker.id" class="cm-wk">
            <span class="rm-avatar">{{ worker.initials }}</span>
            <span class="cm-wk__text">
              <span class="cm-wk__name">{{ worker.name }}</span>
              <span class="rm-muted cm-wk__meta rm-num">{{ worker.meta }}</span>
              <span class="cm-wk__shifts">
                <span v-for="shift in worker.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                  {{ shift.quantity }}
                </span>
              </span>
            </span>
          </li>
        </ul>
      </template>
    </ui-sheet>
  </div>
</template>

<script>
import CompanyReportTabReservedMeals from "@/components/company/reports/companyTabs/companyReportTabReservedMeals.vue";
import reportService from "@/services/report";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import "@/components/report/mobile/screens/reportMobileMealCards.scss";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";
import { groupReservedWorkers } from "../../reservedWorkers";

/**
 * Mobilni "Rezervni obroci" firme: obroci sa količinom po smeni; klik na obrok otvara donju karticu sa radnicima
 * koji su ga naručili (po smenama). Nasleđuje učitavanje iz starog taba (koji nema štampu ni Excel).
 */
export default {
  name: "CompanyMobileReserved",
  extends: CompanyReportTabReservedMeals,
  mixins: [hasPeriodRefetch("fetchReservedMeals")],
  components: { ReportShiftTag },
  data: () => ({
    workersSheet: false,
    workersLoading: false,
    workersFailed: false,
    workersData: null,
    workersRequest: 0,
  }),
  computed: {
    rows() {
      const found = this.search ? this.customSearch(this.meals, [{ value: "name" }], this.search) : this.meals;
      return found
        .filter((meal) => meal.total > 0)
        .map((meal, index) => ({
          key: `${meal.id || meal.name}-${index}`,
          source: meal,
          name: this.toLatin(meal.name),
          total: meal.total,
          shifts: SHIFTS.map((shift) => ({ id: shift.id, quantity: meal[shift.key] || 0 })).filter(
            (shift) => shift.quantity > 0
          ),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    mealTotal() {
      return this.rows.reduce((sum, row) => sum + row.total, 0);
    },
    periodText() {
      const { from, to } = this.dates;
      return from === to ? this.localeDate(from) : `${this.localeDate(from)} – ${this.localeDate(to)}`;
    },
    /** Radnici koji su naručili izabrani obrok (iz liste narudžbi firmi), po smenama. */
    workers() {
      return groupReservedWorkers(this.workersData, this.selectedMeal, this.toLatin);
    },
  },
  methods: {
    openWorkers(meal) {
      this.selectedMeal = meal;
      this.workersData = null;
      this.workersSheet = true;
      this.loadWorkers();
    },
    /** Isti podaci kao spisak narudžbi firmi (jedan zahtev za sve izabrane firme). */
    async loadWorkers() {
      const ids = (this.$store.state.report.selectedFoodCompanies || []).map((company) => company.id).join(",");
      if (!ids) {
        return;
      }
      const request = ++this.workersRequest;
      this.workersLoading = true;
      this.workersFailed = false;
      try {
        const { data } = await reportService.getCompanyUserList(ids, { date: this.dates.from, date2: this.dates.to });
        if (request === this.workersRequest) {
          this.workersData = data;
        }
      } catch (error) {
        if (request === this.workersRequest) {
          this.workersFailed = true;
        }
      } finally {
        if (request === this.workersRequest) {
          this.workersLoading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.rm-theme .cm-sheet-sub {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--rm-muted);
}

.rm-theme .cm-retry {
  width: auto;
  padding: 0 20px;
}

.cm-wk {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  &__meta {
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  &__shifts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 4px;
  }
}
</style>
